import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { LazyImage } from '@/components/ui/LazyImage';

export function Framework() {
  const { t } = useTranslation();

  const lenses = [
    {
      id: '01',
      title: t('framework.lens1.title', 'The Enneagram'),
      description: t('framework.lens1.desc', 'Understand core motivations, fears, and internal defense mechanisms that drive behavior.'),
      image: '/enneagram-teal-corrected.webp',
      imageAlt: 'The Enneagram nine-pointed diagram illustrating core personality motivations and defence mechanisms',
      url: '/lens/enneagram'
    },
    {
      id: '02',
      title: t('framework.lens2.title', 'Personal Mastery & Maturity'),
      description: t('framework.lens2.desc', 'Measure resilience, self-efficacy, and leadership maturity (vertical development) to adapt to rapid organizational change.'),
      image: '/personal-mastery-DETAILED.webp',
      imageAlt: 'Personal Mastery and Maturity lens diagram showing self-efficacy, resilience, and vertical leadership development dimensions',
      url: '/lens/personal-mastery'
    },
    {
      id: '03',
      title: t('framework.lens3.title', 'Emotional Resilience'),
      description: t('framework.lens3.desc', 'Assess capacity to adapt, recover, and maintain clarity and effectiveness when under strain.'),
      image: '/resilience-DETAILED.webp',
      imageAlt: 'Emotional Resilience lens diagram showing capacity to adapt, recover, and maintain effectiveness under strain',
      url: '/lens/emotional-resilience'
    },
    {
      id: '04',
      title: t('framework.lens4.title', 'Social Drives'),
      description: t('framework.lens4.desc', 'Analyze how individuals navigate status, connection, and belonging within teams by uncovering fundamental motivations and unmet needs.'),
      image: '/social-drives-DETAILED.webp',
      imageAlt: 'Social Drives lens diagram showing how individuals navigate status, connection, and belonging within teams',
      url: '/lens/social-drives'
    },
    {
      id: '05',
      title: t('framework.lens5.title', 'Energy Centres'),
      description: t('framework.lens5.desc', 'Explore how individuals process information and make decisions using three core centres: Instinctual, Emotional and Intellectual.'),
      image: '/energy-centres-DETAILS.webp',
      imageAlt: 'Energy Centers lens diagram showing Instinctual, Emotional, and Intellectual processing centres',
      url: '/lens/energy-centres'
    }
  ];

  return (
    <section id="framework" className="py-24 md:py-32 bg-gradient-to-br from-rule-3/10 via-white to-rule-3/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24">
          <div className="relative">
            <div className="lg:sticky lg:top-32 lg:py-12">
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-ground-deep text-[10px] font-bold tracking-widest text-rule-3 uppercase rounded-full mb-8">
                  The Methodology
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-[1.1] mb-6 text-ink">
                  {t('framework.mainTitle', 'Beyond Behavioral Typology.')} <br />
                  <span className="text-ink/70 font-light mt-3 block text-3xl md:text-4xl lg:text-5xl">{t('framework.subTitle', 'A Multidimensional Approach.')}</span>
                </h2>
                <p className="text-lg md:text-xl text-ink/70/80 mb-10 leading-relaxed font-light max-w-lg">
                  {t('framework.mainDesc', 'Human behavior is too complex for a single lens. By integrating five distinct layers of psychological insight, the FIVE LENS framework provides unprecedented, data-driven depth for coaching, culture change, and leadership development.')}
                </p>
                <Link to="/framework" className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-ink hover:text-rule-3 transition-colors group">
                  {t('framework.readMore', 'Read the full methodology')}
                  <span className="w-8 h-8 rounded-full border border-ground-deep bg-white flex items-center justify-center group-hover:border-rule-3 group-hover:bg-rule-3 group-hover:text-white transition-all">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </ScrollReveal>
            </div>
          </div>
          
          <div className="flex flex-col gap-6">
            {lenses.map((lens, index) => (
              <motion.div
                key={lens.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl shadow-sm border border-ground-deep hover:shadow-xl hover:shadow-ink/10 hover:border-rule-3 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row h-full">
                  <div className="sm:w-2/5 bg-ground-alt/30 p-8 flex items-center justify-center border-b sm:border-b-0 sm:border-r border-ground-deep relative overflow-hidden group-hover:bg-ground-alt/60 transition-colors">
                    <LazyImage 
                      src={lens.image} 
                      alt={lens.imageAlt || lens.title} 
                      className="w-full max-w-[220px] md:max-w-[250px] h-auto object-contain transition-transform duration-700 ease-out group-hover:scale-[1.15]" 
                    />
                  </div>
                  <div className="p-8 sm:p-10 flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[10px] font-bold tracking-[0.2em] text-rule-3/70 uppercase">
                        Lens {lens.id}
                      </span>
                    </div>
                    <h3 className="text-2xl font-serif text-ink mb-4 group-hover:text-rule-3 transition-colors duration-300">
                      {lens.title}
                    </h3>
                    <p className="text-ink/70/80 leading-relaxed font-light mb-8">
                      {lens.description}
                    </p>
                    {lens.url && (
                      <Link 
                        to={lens.url} 
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/70 group-hover:text-rule-3 transition-colors mt-auto"
                      >
                        {t('framework.explore', 'Explore Lens')} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
